import { ICommandBarItemProps, IComponentAsProps, IImageProps, ITooltipHost } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPDefaultButton, IPImage, IPSeparator, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  LibraryDocumentEditDisabledReason,
  useCopilotLibraryDocumentEditor,
} from 'components/Copilot/Panel/Documents/useCopilotLibraryDocumentEditor';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import GenericModal from 'components/Dialogs/GenericModal';
import WordEvidenceExportModal, { canExportWordEvidence } from 'components/Links/Export/WordEvidenceExportModal';
import { PageHeaderDoc } from 'components/SharePoint/PageRenderer/SharePointPageHeaders';
import SharepointFilePicker from 'components/SharePoint/SharepointFilePicker';
import SharepointListItemPicker from 'components/SharePoint/SharepointListItemPicker';
import {
  cancelIcon,
  editIcon,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  helpIcon,
  openExtIcon,
  sharepointIcon,
} from 'globalStyles';
import Activity, { ActivityPriority, ActivityType } from 'models/activity';
import { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import { ResourceListType } from 'models/resourceList';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateActivity } from 'services/Api/activityService';
import { apiGetLinkActivity, apiUpdateLink } from 'services/Api/linkService';
import { apiRefreshLinkContent } from 'services/Api/searchService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import { ISite, ISitePage } from 'services/Graph/SharepointInterfaces';
import { getGraphSharepointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import { graphGetSitePage } from 'services/Graph/graphServicePage';
import { graphGetSite } from 'services/Graph/graphServiceSite';
import logger from 'services/Logging/logService';
import { getHtmlJSX } from 'utils/html';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { getExternalUrl, navigateToExternalUrl } from 'utils/url';
import SharePointPageRenderer from '../../SharePoint/PageRenderer/SharePointPageRenderer';
import { ILinkPreviewProps } from './LinkPreview';
import { LinkPreviewConfirmation } from './LinkPreviewConfirmation';
import { PreviewIFrame } from './PreviewIFrame';

//Custom SharePoint CSS
import 'components/SharePoint/PageRenderer/SharePointPageRenderer.css';

interface IDisabledEditCommandProps {
  buttonProps: IComponentAsProps<ICommandBarItemProps>;
  tooltip: string;
}

const DisabledEditCommand = ({ buttonProps, tooltip }: IDisabledEditCommandProps) => {
  const tooltipRef = useRef<ITooltipHost>(null);
  const DefaultRender = buttonProps.defaultRender;

  return (
    <IPTooltipHost
      componentRef={tooltipRef}
      content={tooltip}
      styles={{ root: { display: 'flex', height: '100%', position: 'relative' } }}
    >
      {DefaultRender !== undefined ? <DefaultRender {...buttonProps} /> : null}
      <span
        onClick={() => tooltipRef.current?.show()}
        style={{ position: 'absolute', inset: 0, cursor: 'not-allowed' }}
      />
    </IPTooltipHost>
  );
};

export const LinkPreviewSharePoint = (props: ILinkPreviewProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['library', 'translation', 'copilot']);
  const [documentPreviewRevision, setDocumentPreviewRevision] = useState<number>(0);
  // The editor only drives the command bar (Edit button + edit modal); when the bar is hidden (e.g. the Copilot
  // panel embeds this preview), skip its write-access check instead of duplicating the panel's own check.
  const documentEditor = useCopilotLibraryDocumentEditor(props.link, {
    enabled: !props.hideCommandbar,
    onDocumentSaved: () => setDocumentPreviewRevision((revision) => revision + 1),
    onEditorOpenChange: props.onDocumentEditorOpenChange,
  });
  const [webUrl, setWebUrl] = useState<string | undefined>(undefined);
  const [embedUrl, setEmbedUrl] = useState<string | undefined>(undefined);
  //Starts true: loadData runs from an effect, so the first committed render happens before it can flip this.
  //Starting false made that render fall through to the "not available" pane - which shows unavailableText, i.e.
  //a preview-error flash on every (re)load before the spinner appeared.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<ISitePage | undefined>(undefined);
  const [showConfirmOpen, setShowConfirmOpen] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [activity, setActivity] = useState<Activity | undefined>(undefined);
  const [iFrameLoadError, setIFrameLoadError] = useState<boolean>(false);
  const [fitToWidth, setFitToWidth] = useState<boolean>(true);
  const [showWordEvidenceExport, setShowWordEvidenceExport] = useState<boolean>(false);

  const imageFileNotAvailable: IImageProps = {
    src: `${Config.getImageURL()}/file-not-available.png`,
    imageFit: IPImageFit.contain,
    width: 96,
    height: 96,
  };

  //
  // Effects
  //
  useEffect(() => {
    const fit = getLocalStorageData(appContext, LocalStorageKeys.PageContentFitToWidth);
    if (fit === 'false') {
      setFitToWidth(false);
    } else {
      setFitToWidth(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.link) {
      loadData();
    } else {
      //Nothing to load, so nothing will clear the initial loading state.
      setIsLoading(false);
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.link, documentPreviewRevision]);

  useEffect(() => {
    if (iFrameLoadError && embedUrl) {
      //when there is an iframe load error, open the link in a new tab
      //when the list has the SkipPreview setting enabled, threat this as an iframe load error
      navigateToExternalUrl(embedUrl, undefined, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iFrameLoadError]);

  //
  // Loading
  //
  const loadData = async () => {
    try {
      if (!props.link) return;
      setIsLoading(true);
      clear();

      logger.debug('Getting link preview URLs for ' + props.link.linkName);

      if (props.link.list.listType === ResourceListType.SitePageLibrary) {
        const page = await GetPage(props.link);
        const webUrl = page?.webUrl;
        setWebUrl(webUrl);
        setPage(page);
      } else if (!props.link.isWholeList()) {
        const webUrl = await props.link.getWebURL(appContext);
        const embedUrl = await props.link.getEmbedURL(appContext);
        setWebUrl(webUrl);
        setEmbedUrl(embedUrl);
      } else {
        const webUrl: string = props.link.list.webURL || '';
        const embedUrl: string = webUrl;
        if (webUrl) setWebUrl(webUrl);
        if (embedUrl) setEmbedUrl(embedUrl);
      }

      //
      // When the SkipPreview setting is enabled, open the embed URL in a new tab
      //
      if (props.link.list.skipPreview) {
        setIFrameLoadError(true);
      }

      refreshLinkContentIndex(props.link);

      //let the parent know that the version info is loaded
      props.onVersionLoaded?.(props.link.versionInfo);
      //create read response activity
      createReadResponseActivity();
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        //ignore known errors
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Links
  //
  const openLink = () => {
    if (props.confirmOpen === true) {
      setShowConfirmOpen(true);
    } else {
      doOpenLink();
    }
  };

  const doOpenLink = () => {
    setShowConfirmOpen(false);
    if (!webUrl) return;
    navigateToExternalUrl(webUrl, undefined, '');
  };

  const refreshLinkContentIndex = async (link: ResourceLink) => {
    if (!canRefreshLinkContentIndex(link)) return;

    try {
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      await apiRefreshLinkContent(link, accessToken);
    } catch (err) {
      logger.debug('refreshLinkContentIndex', err);
    }
  };

  const canRefreshLinkContentIndex = (link: ResourceLink): boolean => {
    return (
      link.linkId > 0 &&
      link.list.isVirtual !== true &&
      !link.isWholeList() &&
      (link.list.listType === ResourceListType.DocumentLibrary ||
        link.list.listType === ResourceListType.SitePageLibrary)
    );
  };

  //
  // Command bar
  //
  const getCommandBar = () => {
    if (props.hideCommandbar) return null;
    const items = getCommandBarItems();
    const overflowItems = getCommandBarOverflowItems();
    if (items.length === 0 && overflowItems.length === 0) return null;

    return (
      <IPStack.Item>
        <IPCommandBar items={items} overflowItems={overflowItems} />
        <GenericModal
          title={t('library:PinnedLinkTabs.Dialogs.Help.Title')}
          isOpen={showHelp}
          onClose={() => setShowHelp(false)}
          minWidth={650}
          verticalGap={globalStackTokensGapSmall}
        >
          <IPSeparator />
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPText block as="div">
              {getHtmlJSX(getHelpText())}
            </IPText>
          </IPStack>
        </GenericModal>
        {documentEditor.editorModal}
      </IPStack.Item>
    );
  };

  const getEditDisabledTooltip = (reason: LibraryDocumentEditDisabledReason): string | undefined => {
    switch (reason) {
      case 'type':
        return t('library:PinnedLinkTabs.Commands.EditDisabledType');
      case 'noaccess':
        return t('library:PinnedLinkTabs.Commands.EditDisabledNoAccess');
      case 'busy':
        return t('library:PinnedLinkTabs.Commands.EditDisabledBusy');
      default:
        return undefined;
    }
  };

  const getCommandBarItems = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [];

    const editDisabledTooltip = getEditDisabledTooltip(documentEditor.disabledReason);

    items.push({
      key: 'editDocument',
      text: t('library:PinnedLinkTabs.Commands.EditDocument'),
      iconProps: editIcon,
      disabled: !webUrl || documentEditor.disabledReason !== undefined,
      title: editDisabledTooltip,
      onClick: documentEditor.beginEdit,
      commandBarButtonAs:
        editDisabledTooltip === undefined
          ? undefined
          : (buttonProps) => <DisabledEditCommand buttonProps={buttonProps} tooltip={editDisabledTooltip} />,
    });

    if (props.link?.list.listType === ResourceListType.WebURL) {
      items.push({
        key: 'open',
        disabled: !webUrl,
        text: t('library:PinnedLinkTabs.Commands.OpenInWeb'),
        iconProps: openExtIcon,
        onClick: openLink,
      });
    } else {
      items.push({
        key: 'open',
        disabled: !webUrl,
        text: t('library:PinnedLinkTabs.Commands.OpenInSharePoint'),
        iconProps: sharepointIcon,
        onClick: openLink,
      });
    }

    if (
      !props.hideExport &&
      page &&
      props.link?.linkId &&
      props.link?.list.listType === ResourceListType.SitePageLibrary
    ) {
      items.push({
        key: 'exportEvidencePdf',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.ExportEvidencePdf'),
        iconProps: { iconName: 'Print' },
        onClick: () => {
          window.open(`/library/export/${props.link?.linkId}`, '_blank', 'noopener,noreferrer');
        },
      });
    }

    if (!props.hideExport && canExportWordEvidence(props.link)) {
      items.push({
        key: 'exportWordEvidencePdf',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.ExportEvidencePdf'),
        iconProps: { iconName: 'Print' },
        onClick: () => {
          setShowWordEvidenceExport(true);
        },
      });
    }

    if (props.onClose) {
      items.push({
        key: 'close',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.Close'),
        iconProps: cancelIcon,
        onClick: () => {
          if (props.onClose) props.onClose();
        },
      });
    }

    if (props.extraCommandBarButtons) {
      items.push(...props.extraCommandBarButtons);
    }

    return items;
  };

  const getCommandBarOverflowItems = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [];

    if (page) {
      //show the fit-to-width button only if the page is available
      items.push({
        key: 'fitToWidth',
        disabled: false,
        checked: fitToWidth,
        canCheck: true,
        text: t('library:PinnedLinkTabs.Commands.FitToWidth'),
        iconProps: { iconName: 'FitWidth' },
        onClick: () => {
          const newFitToWidth = !fitToWidth;
          setFitToWidth(newFitToWidth);
          setLocalStorageData(appContext, LocalStorageKeys.PageContentFitToWidth, newFitToWidth.toString());
        },
      });
    }

    if (props.link && !props.hideId) {
      const linkId = props.link.linkId;
      const linkUrl = `/library/links?id=${linkId}`;

      items.push({
        key: 'copyId',
        disabled: false,
        text: t('translation:CopyId.Tooltip', { id: linkId }),
        iconProps: { iconName: 'Copy' },
        onClick: () => {
          navigator.clipboard.writeText(linkId.toString());
        },
      });

      items.push({
        key: 'copyLink',
        disabled: false,
        text: t('translation:CopyLink.Tooltip'),
        iconProps: { iconName: 'Link' },
        onClick: () => {
          const externalUrl = getExternalUrl(linkUrl, appContext.user.tenant, appContext.user.login.tenantId);
          navigator.clipboard.writeText(externalUrl);
        },
      });
    }

    if (!props.hideHelp) {
      items.push({
        key: 'help',
        disabled: false,
        text: t('library:PinnedLinkTabs.Commands.Help'),
        iconProps: helpIcon,
        onClick: () => {
          setShowHelp(true);
        },
      });
    }

    return items;
  };

  //
  // Helpers
  //
  const GetPage = async (link: ResourceLink): Promise<ISitePage | undefined> => {
    if (link && link.list.listType === ResourceListType.SitePageLibrary && link.list.spSiteId && link.pageId) {
      //get the graph client
      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, link.list.altTenantId);

      //get the page with web parts
      const page = await graphGetSitePage(graphInterface.client, link.list.spSiteId, link.pageId, true);

      //fallback to the link URL if the page does not have a webUrl
      if (!page.webUrl) {
        page.webUrl = link.linkURL;
      }

      //fix page url. in some cases Graph returns a relative url
      if (page.webUrl && !page.webUrl.startsWith('https://')) {
        logger.debug('Fix page web url', page.webUrl);
        const site: ISite = await graphGetSite(graphInterface.client, link.list.spSiteId);
        if (site.webUrl) {
          page.webUrl = site.webUrl + '/' + page.webUrl;
        }
      }

      //update the link with the new URL from SharePoint
      if (page.webUrl !== link.linkURL && link.list) {
        try {
          link.linkURL = page.webUrl;
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          await apiUpdateLink(link, true, accessToken, appContext.globalDataCache);
        } catch (err) {
          logger.debug('Error update link after getting web url', err);
        }
      }

      //update metadata
      link.setMetaDataForPage(page);

      return page;
    }

    return undefined;
  };

  const clear = () => {
    setWebUrl(undefined);
    setEmbedUrl(undefined);
    setPage(undefined);
    setIFrameLoadError(false);
    setActivity(undefined);
  };

  const createReadResponseActivity = async () => {
    try {
      if (!props.link) return;

      let newVersionActivity: Activity | undefined;
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      //check whether we have an activity set on the link we can use, otherwise retrieve it
      if (
        !props.link.activity ||
        !props.link.activity.activityId ||
        !props.link.activity.entity ||
        props.link.activity.entity.entityId !== props.link.linkId
      ) {
        newVersionActivity = await apiGetLinkActivity(props.link, ActivityType.LibraryNewVersion, accessToken);
      } else {
        newVersionActivity = props.link.activity;
      }

      setActivity(newVersionActivity);
      let activity: Activity | undefined = undefined;

      //Back-end logic:
      //---------------
      //When there is approval support of latest version
      //------------------------------------------------
      //1. When the latest version approval is not read: return activity with type LibraryNewVersion
      //2. When the latest version approval is read: return activity with type LibraryReadResponse and entityType Activity
      //3. When there is no latest version approval and link is read: return activity with type LibraryReadResponse and entityType Link
      //4. When there is no latest version approval and link is not read: return null

      //When there is NO approval support of latest version
      //---------------------------------------------------
      //1. When link is read: return activity with type LibraryReadResponse and entityType Link
      //2. When link is not read: return null

      if (newVersionActivity && newVersionActivity.typeOfActivity === ActivityType.LibraryNewVersion) {
        //1. When the latest version approval is not read: return activity with type LibraryNewVersion
        //-> send a read response with link to the activity of the new version
        activity = new Activity();
        activity.typeOfActivity = ActivityType.LibraryItemRead;
        activity.entity.typeOfEntity = EntityTypes.Activity;
        activity.entity.entityId = newVersionActivity.activityId;
        activity.entity.entityName = props.link.linkName;
        activity.userId = appContext.user.id;
        activity.priority = ActivityPriority.Normal;
        await apiCreateActivity(activity, true, true, false, accessToken);
      }

      //always create read response
      activity = new Activity();
      activity.typeOfActivity = ActivityType.LibraryItemRead;
      activity.entity.typeOfEntity = EntityTypes.Link;
      activity.entity.entityId = props.link.linkId;
      activity.entity.entityName = props.link.linkName;
      activity.userId = appContext.user.id;
      activity.priority = ActivityPriority.Normal;
      await apiCreateActivity(activity, false, false, false, accessToken);
    } catch (err) {
      logger.debug('createReadResponseActivity', err);
    }
  };

  const getHelpText = (): string => {
    if (!props.link) return '';

    if (props.link.isWholeList()) {
      return t('library:PinnedLinkTabs.Dialogs.Help.WholeList');
    } else {
      switch (props.link?.list.listType) {
        case ResourceListType.WebURL:
          return t('library:PinnedLinkTabs.Dialogs.Help.WebURL');
        case ResourceListType.CustomList:
        case ResourceListType.DocumentLibrary:
          return t('library:PinnedLinkTabs.Dialogs.Help.DocOrList');
        case ResourceListType.SitePageLibrary:
          return t('library:PinnedLinkTabs.Dialogs.Help.Page');
      }
    }
  };

  //
  // Main render
  //
  if (!props.link) return null;

  //
  // For links of type 'whole list' render a file picker or list item picker in view-mode
  //
  if (props.link.isWholeList()) {
    switch (props.link.list.listType) {
      case ResourceListType.DocumentLibrary:
        if (!props.link.list.spDriveId || !props.link.list.spDriveItemId) return null;

        return (
          <IPStack verticalFill tokens={globalStackTokensGapMedium}>
            {getCommandBar()}
            <SharepointFilePicker
              parentFolder={{
                tenantId: props.link.list.altTenantId,
                driveId: props.link.list.spDriveId,
                driveItemId: props.link.list.spDriveItemId,
              }}
              invalidAuthToken={() => {}}
              onSelect={() => {}}
              viewMode={true}
            ></SharepointFilePicker>
          </IPStack>
        );
      case ResourceListType.CustomList:
        if (!props.link.list.spSiteId || !props.link.list.spListId) return null;

        return (
          <IPStack verticalFill tokens={globalStackTokensGapMedium}>
            {getCommandBar()}
            <SharepointListItemPicker
              parentList={{
                tenantId: props.link.list.altTenantId,
                siteId: props.link.list.spSiteId,
                listId: props.link.list.spListId,
              }}
              invalidAuthToken={() => {}}
              onSelect={() => {}}
              viewMode={true}
            ></SharepointListItemPicker>
          </IPStack>
        );
    }
  }

  //
  // For custom list items, render the list with item filter
  //
  if (props.link.list.listType === ResourceListType.CustomList) {
    if (!props.link.list.spSiteId || !props.link.list.spListId) return null;

    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        {getCommandBar()}
        <SharepointListItemPicker
          parentList={{
            tenantId: props.link.list.altTenantId,
            siteId: props.link.list.spSiteId,
            listId: props.link.list.spListId,
          }}
          invalidAuthToken={() => {}}
          onSelect={() => {}}
          viewMode={true}
          filterId={props.link.listItemId}
        ></SharepointListItemPicker>
      </IPStack>
    );
  }

  const onIFrameLoadError = async () => {
    setIFrameLoadError(true);
  };

  //
  // For other individual items, render the Embed-url in an iframe or render page content
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      {getCommandBar()}
      {props.link && (
        <WordEvidenceExportModal
          isOpen={showWordEvidenceExport}
          link={props.link}
          onClose={() => setShowWordEvidenceExport(false)}
        />
      )}
      {!embedUrl && !isLoading && !page && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPImage {...imageFileNotAvailable} />
            </IPStack.Item>
            <IPStack.Item styles={{ root: { maxWidth: '100%' } }}>
              <IPText>{props.unavailableText ?? t('translation:General.Notifications.NotAvailable')}</IPText>
            </IPStack.Item>
            {webUrl && (
              <IPStack.Item styles={{ root: { maxWidth: '100%' } }}>
                <IPDefaultButton
                  iconProps={openExtIcon}
                  onClick={() => navigateToExternalUrl(webUrl, undefined, '')}
                  text={t('library:PinnedLinkTabs.Commands.OpenInWeb')}
                ></IPDefaultButton>
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack>
      )}
      {isLoading && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPSpinner size={IPSpinnerSize.large} />
        </IPStack>
      )}
      {!isLoading && page && (
        <SharePointPageRenderer
          link={props.link}
          page={page}
          activity={activity}
          enableLinks={props.enableLinks}
          owner={props.link}
          fitToWidth={fitToWidth}
          hideHeader={props.hideHeader}
          siteId={props.link.list.spSiteId}
        />
      )}
      {!isLoading && iFrameLoadError && !page && embedUrl && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPImage {...imageFileNotAvailable} />
          </IPStack.Item>
          <IPStack.Item styles={{ root: { maxWidth: '80%' } }}>
            <IPText block styles={globalTextStylesBold}>
              {t('library:Preview.IFrameError')}
            </IPText>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { maxWidth: '80%' } }}>
            <IPDefaultButton
              iconProps={openExtIcon}
              onClick={() => navigateToExternalUrl(embedUrl, undefined, '')}
              text={t('library:PinnedLinkTabs.Commands.OpenInWeb')}
            ></IPDefaultButton>
          </IPStack.Item>
        </IPStack>
      )}
      {!isLoading && !iFrameLoadError && !page && embedUrl && (
        <IPStack verticalFill>
          {props.link.list.listType === ResourceListType.DocumentLibrary && !props.hideHeader && (
            <PageHeaderDoc link={props.link} activity={activity} />
          )}
          <IPStack.Item grow>
            <PreviewIFrame title={props.link.linkName} src={embedUrl} webUrl={webUrl} onError={onIFrameLoadError} />
          </IPStack.Item>
        </IPStack>
      )}
      {props.link?.hasRC() && props.onConfirmed && props.onNext && (
        <IPStack.Item>
          <LinkPreviewConfirmation
            link={props.link}
            onConfirmed={props.onConfirmed}
            onNext={props.onNext}
            version={props.link.versionInfo.version}
            hasNext={true}
          />
        </IPStack.Item>
      )}
      <DialogYesNo
        hidden={!showConfirmOpen}
        onYes={() => doOpenLink()}
        onNo={() => setShowConfirmOpen(false)}
        title={t('library:PinnedLinkTabs.Dialogs.Open.Title')}
        subText={t('library:PinnedLinkTabs.Dialogs.Open.SubText')}
      ></DialogYesNo>
    </IPStack>
  );
};
